import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable } from '@/components/ReportTable';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { PICKUP_STATUS_BADGE, type PickupStatus } from '@/lib/badges';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PickupRow {
  id: number;
  number: string;
  customer_name: string;
  date: string;
  expected_pickup_date: string | null;
  pickup_status: PickupStatus;
  overdue: number;
  remaining_qty: number;
}

interface PickupsData {
  rows: PickupRow[];
}

interface PickupsResponse {
  data: PickupsData;
}

// ─── Status filter ───────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'awaiting' | 'partial' | 'overdue' | 'complete';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'awaiting', label: 'Awaiting' },
  { value: 'partial', label: 'Partial' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'complete', label: 'Complete' },
];

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS = [
  {
    key: 'number',
    header: 'Number',
    render: (value: unknown, row: Record<string, unknown>) => (
      <Link
        to={`/estimates/${row.id}`}
        className="text-primary hover:underline underline-offset-4 font-medium"
      >
        {String(value)}
      </Link>
    ),
  },
  { key: 'customer_name', header: 'Customer' },
  {
    key: 'date',
    header: 'Date',
    render: (value: unknown) => (value ? formatDate(String(value)) : '—'),
  },
  {
    key: 'expected_pickup_date',
    header: 'Expected Pickup',
    render: (value: unknown, row: Record<string, unknown>) => {
      if (value == null) return <span className="text-muted-foreground">—</span>;
      const isOverdue = Number(row.overdue) === 1;
      return (
        <span className={isOverdue ? 'text-red-600 font-medium' : undefined}>
          {formatDate(String(value))}
        </span>
      );
    },
  },
  { key: 'remaining_qty', header: 'Remaining Qty', align: 'right' as const },
  {
    key: 'pickup_status',
    header: 'Status',
    render: (value: unknown) => {
      const status = value as PickupStatus;
      const cfg = PICKUP_STATUS_BADGE[status];
      return cfg ? <Badge variant={cfg.variant}>{cfg.label}</Badge> : '—';
    },
  },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function PickupsReport() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params = {
    status,
    from: from || undefined,
    to: to || undefined,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'pickups', status, from, to],
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('status', status);
      if (from) sp.set('from', from);
      if (to) sp.set('to', to);
      return apiFetch<PickupsResponse>(`/api/reports/pickups?${sp.toString()}`);
    },
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Pickups</h1>
        <ExportButtons endpoint="/api/reports/pickups" params={params} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === f.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="from-input" className="text-sm text-muted-foreground">
            From
          </Label>
          <Input
            id="from-input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-36 h-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="to-input" className="text-sm text-muted-foreground">
            To
          </Label>
          <Input
            id="to-input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-36 h-8 text-sm"
          />
        </div>
      </div>

      {/* Loading / error */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && <ReportTable columns={COLUMNS} rows={rows} keyField="id" />}
    </div>
  );
}
